import { Button, InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot, Loader2 } from '@chain-zen/ui'
import * as React from 'react'

interface CodeVerificationInputProps {
  emailOrPhone: string
  loading: boolean
  onResendCode: (params: { countryCode: string; emailOrPhone: string }) => Promise<void>
  onSubmit: (code: string) => Promise<{ error: boolean }>
  size?: 'sm' | 'lg'
}

const SLOT_SIZE_CLASSES = {
  lg: 'w-[50px] h-[50px] text-2xl',
  sm: 'w-[40px] h-[40px] text-xl'
}

const renderInputSlots = (size: 'sm' | 'lg') =>
  Array.from({ length: 6 }).map((_, index) => (
    <InputOTPSlot key={index} index={index} className={SLOT_SIZE_CLASSES[size]} />
  ))

export function CodeVerificationInput({
  onSubmit,
  loading,
  emailOrPhone,
  onResendCode,
  size = 'lg'
}: CodeVerificationInputProps) {
  const [code, setCode] = React.useState('')
  const [isResending, setIsResending] = React.useState(false)
  const debounceTimerRef = React.useRef<number | null>(null)

  const handleResendCode = async () => {
    setIsResending(true)
    setCode('')
    try {
      const cleanedEmailPhone = emailOrPhone.includes('@') ? emailOrPhone : emailOrPhone.slice(2)
      await onResendCode({ countryCode: '+1', emailOrPhone: cleanedEmailPhone })
    } finally {
      setIsResending(false)
    }
  }

  const handleCodeChange = async (value: string) => {
    // Clear any existing timeout
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)

    // Immediately update the code state for better UX
    setCode(value)

    // Only debounce the submission, not the state update
    debounceTimerRef.current = window.setTimeout(async () => {
      // Trigger verification once code reaches 6 characters
      if (value.length === 6) {
        try {
          const result = await onSubmit(value)
          if (result.error === false) {
            setCode('')
          }
        } catch {
          setCode('')
        }
      }
    }, 300) // Increased debounce time to handle fast typing better
  }

  const handlePaste = async (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '') // Remove non-digits

    if (pastedData.length > 0) {
      // Take only the first 6 digits if more are pasted
      const codeToSet = pastedData.slice(0, 6)
      setCode(codeToSet)

      // If we have 6 digits, submit immediately
      if (codeToSet.length === 6) {
        try {
          const result = await onSubmit(codeToSet)
          if (result.error === false) {
            setCode('')
          }
        } catch {
          setCode('')
        }
      }
    }
  }

  return (
    <div className="flex flex-col items-center gap-6" data-testid="verification-container">
      <InputOTP
        value={code}
        onChange={handleCodeChange}
        onPaste={handlePaste}
        maxLength={6}
        disabled={loading || isResending}
        data-testid="verification-otp-input"
        autoFocus={true}
      >
        <InputOTPGroup data-testid="verification-otp-group-1">{renderInputSlots(size).slice(0, 3)}</InputOTPGroup>
        <InputOTPSeparator />
        <InputOTPGroup data-testid="verification-otp-group-2">{renderInputSlots(size).slice(3)}</InputOTPGroup>
      </InputOTP>

      <div className="flex flex-col items-center gap-2">
        {!!loading && <Loader2 className="h-4 w-4 animate-spin" data-testid="verification-loader" />}
        <Button
          variant="link"
          onClick={handleResendCode}
          disabled={isResending || loading}
          className="font-semibold"
          data-testid="resend-code-button"
        >
          {isResending ? 'Sending...' : 'Resend Code'}
        </Button>
      </div>
    </div>
  )
}
